import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import PageViewTracker from "@/components/PageViewTracker";
import EngagementTracker from "@/components/EngagementTracker";
import CookieBanner from "@/components/CookieBanner";
import AdminShortcut from "@/components/AdminShortcut";
import SmartInstallPrompt from "@/components/SmartInstallPrompt";
import ChatbotWidget from "@/components/ChatbotWidget";
import { AuthProvider } from "@/contexts/AuthContext";
import LandingPage from "@/pages/LandingPage";
import LegacyUrlRedirect from "@/components/LegacyUrlRedirect";
import aeoRoutes from "@/routes/aeoRoutes";

// Route-level code splitting — landing page loads immediately; every other
// route ships its own chunk on first visit.
const AdminPanel = lazy(() => import("@/components/AdminPanel"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const MagicVerifyPage = lazy(() => import("@/pages/MagicVerifyPage"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const ProjectsMapPage = lazy(() => import("@/pages/ProjectsMapPage"));
const EmaLandingPage = lazy(() => import("@/pages/EmaLandingPage"));
const BlogListPage = lazy(() => import("@/pages/BlogListPage"));
const BlogArticlePage = lazy(() => import("@/pages/BlogArticlePage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const CookiesPage = lazy(() => import("@/pages/CookiesPage"));
const CityServicePage = lazy(() => import("@/pages/CityServicePage"));
const ReschedulePage = lazy(() => import("@/pages/ReschedulePage"));
const DomNaKlucPage = lazy(() => import("@/pages/DomNaKlucPage"));
const RekonstrukciaBytuPage = lazy(() => import("@/pages/RekonstrukciaBytuPage"));
const ZateplenieCenaPage = lazy(() => import("@/pages/ZateplenieCenaPage"));
const ServiceDetailPage = lazy(() => import("@/pages/ServiceDetailPage"));

// SUPERMAJSTRI OS — internal construction management platform
const OsLayout       = lazy(() => import("@/pages/os/OsLayout"));
const OsDashboard    = lazy(() => import("@/pages/os/Dashboard"));
const OsCustomers    = lazy(() => import("@/pages/os/CustomersList"));
const OsCustomer     = lazy(() => import("@/pages/os/CustomerDetail"));
const OsLeads        = lazy(() => import("@/pages/os/LeadsList"));
const OsLead         = lazy(() => import("@/pages/os/LeadDetail"));
const OsProjects     = lazy(() => import("@/pages/os/ProjectsList"));
const OsProject      = lazy(() => import("@/pages/os/ProjectDetail"));
const OsSettings     = lazy(() => import("@/pages/os/SettingsHome"));
const OsInventory    = lazy(() => import("@/pages/os/InventoryDashboard"));
const OsExecution    = lazy(() => import("@/pages/os/ExecutionDashboard"));
const OsLabor        = lazy(() => import("@/pages/os/LaborDashboard"));
const OsNotifications = lazy(() => import("@/pages/os/NotificationsInbox"));
const OsAlerts        = lazy(() => import("@/pages/os/AlertsBoard"));
const OsNotifPrefs    = lazy(() => import("@/pages/os/NotificationPreferences"));
const OsCostDashboard = lazy(() => import("@/pages/os/CostControlDashboard"));
const OsProjectCost   = lazy(() => import("@/pages/os/ProjectCost"));
const OsAutomation    = lazy(() => import("@/pages/os/AutomationDashboard"));
const OsAutomationDetail = lazy(() => import("@/pages/os/AutomationDetail"));
const OsManagement    = lazy(() => import("@/pages/os/ManagementDashboard"));
const OsProjectIntelligence = lazy(() => import("@/pages/os/ProjectIntelligence"));
const OsAIProjectCopilot = lazy(() => import("@/pages/os/AIProjectCopilot"));
const OsAIManagement = lazy(() => import("@/pages/os/AIManagement"));
const OsAIBriefings  = lazy(() => import("@/pages/os/AIBriefings"));
const OsProfile      = lazy(() => import("@/pages/os/ProfilePage"));
const OsCalcDetail   = lazy(() => import("@/pages/os/calculations/CalculationDetail"));

// AEO / hyperlocal / pillar pages are registered in /src/routes/aeoRoutes.js.
// We convert each importer → React.lazy component here (memoized once at module load).
const aeoLazyRoutes = aeoRoutes.map(({ path, importer }) => ({
  path,
  Component: lazy(importer),
}));

const RouteFallback = () => (
  <div className="min-h-screen bg-black text-white grid place-items-center">
    <div className="text-sm text-zinc-400 animate-pulse">Načítavam…</div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PageViewTracker />
        <EngagementTracker />
        <AdminShortcut />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/sluzby/:service/:city" element={<CityServicePage />} />
            <Route path="/sluzby/:slug" element={<ServicePage />} />

            {/* AEO / hyperlocal / pillar pages — see /src/routes/aeoRoutes.js */}
            {aeoLazyRoutes.map(({ path, Component }) => (
              <Route key={path} path={path} element={<Component />} />
            ))}

            <Route path="/projekty" element={<ProjectsMapPage />} />
            <Route path="/ema" element={<EmaLandingPage />} />
            <Route path="/blog" element={<BlogListPage />} />
            <Route path="/blog/:slug" element={<BlogArticlePage />} />
            <Route path="/rezervacia/:token" element={<ReschedulePage />} />
            <Route path="/dom-na-kluc" element={<DomNaKlucPage />} />
            <Route path="/rekonstrukcia-bytu" element={<RekonstrukciaBytuPage />} />
            <Route path="/zateplenie" element={<ZateplenieCenaPage />} />
            <Route path="/sluzba/:slug" element={<ServiceDetailPage />} />
            <Route path="/ochrana-sukromia" element={<PrivacyPolicyPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/magic" element={<MagicVerifyPage />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminPanel />
                </ProtectedRoute>
              }
            />
            {/* SUPERMAJSTRI OS — internal ops platform (requires any OS role) */}
            <Route path="/os" element={<ProtectedRoute><OsLayout /></ProtectedRoute>}>
              <Route index element={<OsDashboard />} />
              <Route path="customers" element={<OsCustomers />} />
              <Route path="customers/:id" element={<OsCustomer />} />
              <Route path="leads" element={<OsLeads />} />
              <Route path="leads/:leadId" element={<OsLead />} />
              <Route path="projects" element={<OsProjects />} />
              <Route path="projects/:projectId" element={<OsProject />} />
              <Route path="projects/:projectId/calculations/:calculationId" element={<OsCalcDetail />} />
              <Route path="settings" element={<OsSettings />} />
              <Route path="inventory" element={<OsInventory />} />
              <Route path="execution" element={<OsExecution />} />
              <Route path="labor" element={<OsLabor />} />
              <Route path="notifications" element={<OsNotifications />} />
              <Route path="notifications/preferences" element={<OsNotifPrefs />} />
              <Route path="alerts" element={<OsAlerts />} />
              <Route path="cost-control" element={<OsCostDashboard />} />
              <Route path="automation" element={<OsAutomation />} />
              <Route path="automation/:automationId" element={<OsAutomationDetail />} />
              <Route path="management" element={<OsManagement />} />
              <Route path="ai" element={<OsAIManagement />} />
              <Route path="ai/briefings" element={<OsAIBriefings />} />
              <Route path="projects/:projectId/intelligence" element={<OsProjectIntelligence />} />
              <Route path="projects/:projectId/ai" element={<OsAIProjectCopilot />} />
              <Route path="projects/:projectId/cost" element={<OsProjectCost />} />
              <Route path="profile" element={<OsProfile />} />
            </Route>
            <Route path="*" element={<LegacyUrlRedirect fallback={LandingPage} />} />
          </Routes>
        </Suspense>
        <CookieBanner />
        <SmartInstallPrompt />
        <ChatbotWidget />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
